import type { ApiAreaOfFocus } from '#/api/types/ApiAreaOfFocus';
import type { ApiVolopFunction } from '#/api/types/ApiVolopFunction';
import type { AppConfig } from '#/config/types/AppConfig';
import { getSearchAlgoliaDefaultConfig } from '#/modules/search/algolia/searchAlgoliaConfig';
import type { SearchAlgoliaVolopHit } from '#/modules/search/algolia/types/SearchAlgoliaVolopHit';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import { algoliaSearch } from '#/utils/algolia/algoliaSearch';

const ATTRIBUTES: (keyof SearchAlgoliaVolopHit)[] = [
  'objectID',
  'type',
  'published',
  'name',
  'city',
  'state',
  'stateStr',
  'country',
  'url',
  'orgID',
  'orgUrl',
  'orgName',
  'orgType',
  'logo',
  'locationType',
  'remoteZone',
  'hasAts',
  'isPostedAnonymously',
];

export async function searchAlgoliaFetchSimilarVolops(
  title: string,
  functions: ApiVolopFunction[],
  userSearchLocation: SearchLocation | undefined,
  orgAreasOfFocus: ApiAreaOfFocus[] | undefined,
  locationType: 'REMOTE' | 'HYBRID' | 'ONSITE' | undefined,
  appConfig: AppConfig,
) {
  function getFilters(filterArray: string[]) {
    if (filterArray.length > 1) {
      return `(${filterArray.join(' OR ')}) AND type:VOLOP`;
    } else if (filterArray.length > 0) {
      return `${filterArray[0]} AND type:VOLOP`;
    }
    return 'type: VOLOP';
  }

  // Optional filters are applied if present, but do not exclude results
  // that do not have them - this allows us to boost remote listings
  // over listings that are further away, but have a high filter score
  const optionalFilters = ['locationType:REMOTE'];

  let aroundLatLng;
  if (userSearchLocation?.latitude && userSearchLocation.longitude) {
    aroundLatLng = `${userSearchLocation.latitude}, ${userSearchLocation.longitude}`;
  }

  const baseOptions = {
    hitsPerPage: 20,
    distinct: true,
    attributesToRetrieve: ATTRIBUTES as string[],
    removeWordsIfNoResults: 'firstWords' as const,
    sumOrFiltersScores: true,
    facets: ['*'],
    optionalFilters,
  };

  const locationSearchOptions = {
    ...baseOptions,
    aroundLatLng,
    aroundRadius: 40000, // approx 25 miles
    filters: getFilters([]),
  };

  const searchConfig = getSearchAlgoliaDefaultConfig({ appConfig });
  // start with location search
  let volopRecommendationsResult = await algoliaSearch<SearchAlgoliaVolopHit>({
    config: searchConfig,
    indexName: appConfig.algolia.siteRecommendationsIndexName,
    query: '',
    options: locationSearchOptions,
    isUsingAlgoliaMock: appConfig.algolia.mock,
  });

  // search cause areas next
  if (volopRecommendationsResult.nbHits < 5) {
    const areasOfFocusFilter =
      orgAreasOfFocus && orgAreasOfFocus.length > 0
        ? orgAreasOfFocus
            .map(
              (areaOfFocus: ApiAreaOfFocus) =>
                `areasOfFocus:${areaOfFocus}<score=1>`,
            )
            .join(' OR ')
        : '';

    const areasOfFocusOptions = {
      ...baseOptions,
      filters: getFilters([areasOfFocusFilter].filter(Boolean)),
    };

    volopRecommendationsResult = await algoliaSearch<SearchAlgoliaVolopHit>({
      config: searchConfig,
      query: title,
      indexName: appConfig.algolia.sitePublishedDescIndexName,
      options: areasOfFocusOptions,
      isUsingAlgoliaMock: appConfig.algolia.mock,
    });
  }

  // search skills next
  if (volopRecommendationsResult.nbHits < 5) {
    const functionsFilter =
      functions && functions.length > 0
        ? functions
            .map((f: ApiVolopFunction) => `functions:${f}<score=10>`)
            .join(' OR ')
        : '';

    const functionsOptions = {
      ...baseOptions,
      filters: getFilters([functionsFilter].filter(Boolean)),
    };

    volopRecommendationsResult = await algoliaSearch<SearchAlgoliaVolopHit>({
      config: searchConfig,
      query: title,
      indexName: appConfig.algolia.sitePublishedDescIndexName,
      options: functionsOptions,
      isUsingAlgoliaMock: appConfig.algolia.mock,
    });
  }

  // finally, fall back to same remote type
  if (volopRecommendationsResult.nbHits < 5) {
    const locationTypeFilter = [
      `locationType:${locationType ? locationType : 'ONSITE'}`,
    ];

    const locationTypeOptions = {
      ...baseOptions,
      filters: getFilters(locationTypeFilter.filter(Boolean)),
    };

    volopRecommendationsResult = await algoliaSearch<SearchAlgoliaVolopHit>({
      config: searchConfig,
      query: title,
      indexName: appConfig.algolia.sitePublishedDescIndexName,
      options: locationTypeOptions,
      isUsingAlgoliaMock: appConfig.algolia.mock,
    });
  }

  return volopRecommendationsResult;
}
